import { onCLS, onFCP, onINP, onLCP, onTTFB, type Metric } from 'web-vitals';

type TelemetryEvent =
  | { type: 'error'; message: string; stack?: string; url: string }
  | { type: 'vital'; name: Metric['name']; value: number; rating: Metric['rating']; url: string };

const ENDPOINT = '/api/v1/telemetry';
const MAX_BATCH = 20;
const FLUSH_MS = 5_000;
const MAX_ERRORS_PER_SESSION = 25;

const queue: TelemetryEvent[] = [];
const seenErrors = new Set<string>();
let timer: ReturnType<typeof setTimeout> | undefined;

/** Path only: query strings can carry secrets (e.g. password-reset tokens) and must never leave the page. */
const currentPath = (): string => window.location.pathname.slice(0, 500);

export function flush(): void {
  if (timer) clearTimeout(timer);
  timer = undefined;
  while (queue.length > 0) {
    const body = JSON.stringify({ events: queue.splice(0, MAX_BATCH) });
    // sendBeacon survives page unload; fall back to keepalive fetch where it is unavailable or refused.
    const sent = typeof navigator.sendBeacon === 'function' && navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'application/json' }));
    if (!sent) {
      void fetch(ENDPOINT, { method: 'POST', body, headers: { 'Content-Type': 'application/json' }, keepalive: true, credentials: 'same-origin' }).catch(() => undefined);
    }
  }
}

function track(event: TelemetryEvent): void {
  queue.push(event);
  if (queue.length >= MAX_BATCH) flush();
  else timer ??= setTimeout(flush, FLUSH_MS);
}

export function reportError(error: unknown): void {
  const err = error instanceof Error ? error : new Error(typeof error === 'string' ? error : 'Non-error thrown');
  const fingerprint = `${err.name}:${err.message}`;
  if (seenErrors.has(fingerprint) || seenErrors.size >= MAX_ERRORS_PER_SESSION) return;
  seenErrors.add(fingerprint);
  track({ type: 'error', message: fingerprint.slice(0, 1000), ...(err.stack ? { stack: err.stack.slice(0, 4000) } : {}), url: currentPath() });
}

export function initTelemetry(): void {
  window.addEventListener('error', (e) => reportError(e.error ?? e.message));
  window.addEventListener('unhandledrejection', (e) => reportError(e.reason));
  const vital = (m: Metric) => track({ type: 'vital', name: m.name, value: Math.round(m.value * 1000) / 1000, rating: m.rating, url: currentPath() });
  onCLS(vital);
  onFCP(vital);
  onINP(vital);
  onLCP(vital);
  onTTFB(vital);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
}
